<script setup lang="ts">
import { formatDateOnly, formatDateOnlyLong } from '@metapic-apps/shared'
import { ElIcon, ElPopover } from 'element-plus'

import { useGetInvoices } from '@/api/invoices.api'
import DownloadIcon from '@/assets/icons/download.svg?component'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import type { AdvertiserInvoice } from '@/lib/types/advertiser.types'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  LoadingComponent,
  TagComponent,
  TooltipComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{
  advertiserId: number
}>()

const { isLoading, data: invoices } = useGetInvoices(props.advertiserId)

const statusLabel = (invoice: AdvertiserInvoice): string => {
  switch (invoice.status) {
    case 'paid':
      return 'Paid'
    case 'failed':
      return 'Failed'
    case 'credited':
      return 'Credited'
    case 'pending':
      return 'Pending'
    default:
      return 'Unknown'
  }
}

const statusTagVariant = (invoice: AdvertiserInvoice): InstanceType<typeof TagComponent>['variant'] => {
  switch (invoice.status) {
    case 'paid':
      return 'success'
    case 'failed':
      return 'danger'
    case 'credited':
      return 'warning'
    case 'pending':
    default:
      return 'primary'
  }
}
</script>

<template>
  <LoadingComponent v-if="isLoading" />
  <BoxComponent v-else>
    <FlexComponent v-if="invoices?.length" direction="column" :gap="8">
      <BoxComponent v-for="invoice in invoices" :key="invoice.id" :p="16" w="100%" class="advertiser-details-invoices__list-item">
        <FlexComponent :gap="8" justify="space-between" align="center">
          <BoxComponent w="100%" data-test-id="invoice-amount-cell">
            <TypographyComponent variant="title-large">
              {{ formatMoneyForAdmin({ amount: invoice.amount, currency: invoice.currency }, { allowZero: true }) }}
            </TypographyComponent>
          </BoxComponent>
          <BoxComponent w="75%" data-test-id="invoice-date-cell">
            <TypographyComponent variant="label-medium" text-align="center">Invoice date</TypographyComponent>
            <TypographyComponent variant="title-large" text-align="center">
              {{ formatDateOnly(invoice.date) }}
            </TypographyComponent>
          </BoxComponent>
          <BoxComponent w="75%" data-test-id="invoice-id-cell">
            <TypographyComponent variant="label-medium" text-align="center">Invoice ID</TypographyComponent>
            <TypographyComponent variant="title-large" text-align="center">{{ invoice.id }}</TypographyComponent>
          </BoxComponent>
          <BoxComponent w="50%" data-test-id="invoice-status-cell">
            <FlexComponent flex="1" :gap="8" justify="space-between" align="center">
              <TooltipComponent :disabled="invoice.status !== 'paid'">
                <TagComponent :variant="statusTagVariant(invoice)">{{ statusLabel(invoice) }}</TagComponent>
                <template #content>
                  <TypographyComponent v-if="invoice.paidAt" variant="body-small" data-test-id="status-tooltip-content"
                    >Paid {{ formatDateOnlyLong(invoice.paidAt) }}</TypographyComponent
                  >
                </template>
              </TooltipComponent>
              <ElPopover v-if="invoice.creditInvoiceUrl" trigger="click" width="auto">
                <template #reference>
                  <ButtonComponent is-icon variant="transparent" :icon-size="24" icon-color="var(--primary)" type="button">
                    <DownloadIcon />
                  </ButtonComponent>
                </template>
                <FlexComponent direction="column" align="stretch" :gap="8">
                  <template
                    v-for="document in [
                      { name: 'Credit Note', url: invoice.creditInvoiceUrl },
                      { name: 'Invoice', url: invoice.url },
                    ]"
                    :key="document.name"
                  >
                    <ButtonComponent variant="transparent" type="submit" :href="document.url" target="_blank">
                      <template #iconLeft>
                        <ElIcon size="24px">
                          <DownloadIcon />
                        </ElIcon>
                      </template>
                      <TypographyComponent variant="title-medium">{{ document.name }}</TypographyComponent>
                    </ButtonComponent>
                  </template>
                </FlexComponent>
              </ElPopover>
              <ButtonComponent
                is-icon
                variant="transparent"
                :icon-size="24"
                icon-color="var(--primary)"
                type="submit"
                :href="invoice.url"
                target="_blank"
              >
                <DownloadIcon />
              </ButtonComponent>
            </FlexComponent>
          </BoxComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
    <BoxComponent v-else :pl="16" :pr="16" :pt="48" :pb="48">
      <BoxComponent :mb="24">
        <TypographyComponent variant="body-large" color="var(--secondary-48)" text-align="center">No invoices yet.</TypographyComponent>
      </BoxComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style scoped>
.advertiser-details-invoices__list-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);
  }
}
</style>
